import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, CalendarClock, Trash2, Check, X as XIcon, Phone, Mail, User2 } from "lucide-react";

const TYPES = [
    { code: "parent", label: "Prind" },
    { code: "institution", label: "Institucion" },
    { code: "company", label: "Kompani" },
    { code: "teacher", label: "Mësues" },
    { code: "other", label: "Tjetër" },
];

const STATUS_TONES = {
    scheduled: "bg-seablue/10 text-seablue border-seablue/30",
    completed: "bg-sage/15 text-sage border-sage/30",
    cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function AdministrataAppointments() {
    const { user } = useAuth();
    const backHome = user?.role === "drejtoresha" ? "/drejtoresha" : user?.role === "super_admin" ? "/admin" : "/administrata";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/administrata/director-appointments");
            setItems(r.data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const setStatus = async (aid, status) => {
        try { await api.patch(`/administrata/director-appointment/${aid}/status`, { status }); refresh(); }
        catch (e) { alert("Gabim: " + (e.response?.data?.detail || e.message)); }
    };
    const del = async (aid) => {
        if (!window.confirm("Fshi këtë takim?")) return;
        await api.delete(`/administrata/director-appointment/${aid}`);
        refresh();
    };

    const now = new Date().toISOString();
    const upcoming = items.filter((a) => a.scheduled_at >= now && a.status !== "cancelled");
    const past = items.filter((a) => !(a.scheduled_at >= now && a.status !== "cancelled"));

    return (
        <Layout>
            <Link to={backHome} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="appts-back">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Takime me Drejtoreshën</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Orar i Takimeve</h1>
                    <p className="mt-1 text-slate-600 text-sm">Cakto takime për prindër, institucione, kompani.</p>
                </div>
                <AppointmentDialog onDone={refresh} />
            </div>

            <section className="mb-6">
                <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">Të ardhshme ({upcoming.length})</h2>
                {loading ? <div className="text-sm text-slate-500">Duke ngarkuar…</div> :
                 upcoming.length === 0 ? <div className="bg-white border border-slate-200 rounded-2xl p-6 text-sm text-slate-500 text-center">Asnjë takim i ardhshëm.</div> : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {upcoming.map((a) => <Card key={a.id} a={a} onStatus={setStatus} onDelete={del} />)}
                    </div>
                )}
            </section>

            {past.length > 0 && (
                <section>
                    <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">Të kaluara / Anulluara ({past.length})</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {past.map((a) => <Card key={a.id} a={a} onStatus={setStatus} onDelete={del} />)}
                    </div>
                </section>
            )}
        </Layout>
    );
}

const Card = ({ a, onStatus, onDelete }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`appt-card-${a.id}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-oxford/10 text-oxford flex items-center justify-center shrink-0">
                    <CalendarClock className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{a.requester_name}</div>
                    <div className="text-[11px] text-slate-500 uppercase tracking-widest">{(TYPES.find((t) => t.code === a.requester_type) || {}).label}</div>
                </div>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${STATUS_TONES[a.status] || STATUS_TONES.scheduled}`}>
                {a.status === "completed" ? "Kryer" : a.status === "cancelled" ? "Anulluar" : "I planifikuar"}
            </span>
        </div>
        <div className="text-sm text-slate-800 mb-1"><strong>Tema:</strong> {a.topic}</div>
        <div className="text-xs text-oxford font-mono">{new Date(a.scheduled_at).toLocaleString("sq-AL")} • {a.duration_minutes}m</div>
        {(a.requester_phone || a.requester_email) && (
            <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                {a.requester_phone && <a href={`tel:${a.requester_phone}`} className="inline-flex items-center gap-1 text-seablue hover:underline"><Phone className="w-3 h-3" /> {a.requester_phone}</a>}
                {a.requester_email && <a href={`mailto:${a.requester_email}`} className="inline-flex items-center gap-1 text-seablue hover:underline"><Mail className="w-3 h-3" /> {a.requester_email}</a>}
            </div>
        )}
        {a.note && <div className="mt-2 text-[11px] text-slate-600 bg-bone/40 p-2 rounded">{a.note}</div>}
        <div className="mt-3 flex items-center gap-2">
            {a.status === "scheduled" && (
                <>
                    <button onClick={() => onStatus(a.id, "completed")} className="text-xs font-semibold text-sage inline-flex items-center gap-1 hover:underline" data-testid={`appt-complete-${a.id}`}>
                        <Check className="w-3 h-3" /> Kryer
                    </button>
                    <button onClick={() => onStatus(a.id, "cancelled")} className="text-xs font-semibold text-terracotta inline-flex items-center gap-1 hover:underline" data-testid={`appt-cancel-${a.id}`}>
                        <XIcon className="w-3 h-3" /> Anulo
                    </button>
                </>
            )}
            <button onClick={() => onDelete(a.id)} className="ml-auto text-xs text-slate-500 inline-flex items-center gap-1 hover:text-terracotta" data-testid={`appt-delete-${a.id}`}>
                <Trash2 className="w-3 h-3" /> Fshi
            </button>
        </div>
    </div>
);

const AppointmentDialog = ({ onDone }) => {
    const [open, setOpen] = useState(false);
    const blank = { requester_name: "", requester_type: "parent", requester_phone: "", requester_email: "", topic: "", scheduled_at: "", duration_minutes: 30, note: "" };
    const [form, setForm] = useState(blank);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const iso = form.scheduled_at ? new Date(form.scheduled_at).toISOString() : "";
            await api.post("/administrata/director-appointment", { ...form, scheduled_at: iso });
            setOpen(false); setForm(blank);
            onDone && onDone();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setErr(""); }}>
            <DialogTrigger asChild>
                <Button className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="appts-add-btn">
                    <Plus className="w-4 h-4" /> Cakto Takim
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Cakto takim me Drejtoreshën</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <Input placeholder="Emri i kërkuesit (prind, institucion…)" value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} required data-testid="appt-name" />
                    <select value={form.requester_type} onChange={(e) => setForm({ ...form, requester_type: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="appt-type">
                        {TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </select>
                    <Input placeholder="Telefon (opsional)" value={form.requester_phone} onChange={(e) => setForm({ ...form, requester_phone: e.target.value })} data-testid="appt-phone" />
                    <Input type="email" placeholder="Email (opsional)" value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} data-testid="appt-email" />
                    <Input placeholder="Tema e takimit" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} required data-testid="appt-topic" />
                    <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} required data-testid="appt-when" />
                    <Input type="number" min={15} max={180} step={15} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })} data-testid="appt-duration" />
                    <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Shënim (opsional)…"
                        rows={2} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="appt-note" />
                    {err && <div className="text-xs text-terracotta">{err}</div>}
                    <DialogFooter>
                        <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="appt-submit">
                            {busy ? "Duke ruajtur…" : "Ruaj"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
